import { usePage as usePageDefault } from '@inertiajs/vue3'
import { computed, watch } from 'vue'
import { toast } from 'vue-sonner'

import type { NavigationItem, User } from '@/Types'

export interface SharedProps {
  // Inertia's PageProps constraint requires the open index signature.
  // oxlint-disable-next-line anti-slop/no-unsafe-dictionary-type
  [key: string]: unknown
  environment: string
  bulky_waste_order_type_labels: Record<App.Enums.BulkyWasteOrderType, string>
  auth: {
    user: User
  }
  url: string
  flash: { bannerStyle: 'success' | 'error'; banner: string } | null
  sidebar_navigation: NavigationItem[]
  topbar_navigation: NavigationItem[]
  can: {
    edit_user_role: boolean
    access_pro_features: boolean
  }
  features: {
    ai_classifier: boolean
    datafleet_issues: boolean
    extended_issue_descriptions: boolean
    issue_comment_images: boolean
    location_match: boolean
  }
}

export function usePage() {
  return usePageDefault<SharedProps>()
}

export function useEnvironment() {
  return computed(() => usePage().props.environment)
}

export function useBulkyWasteOrderTypeLabels() {
  return computed(() => usePage().props.bulky_waste_order_type_labels)
}

export function useIsEnvironment(environment: string) {
  return computed(() => useEnvironment().value === environment)
}

export function useUser() {
  return computed(() => usePage().props.auth.user)
}

export function useFlash() {
  return computed(() => usePage().props.flash)
}

/**
 * Shows server-side flash banners as a toast. Call once per layout.
 */
export function useFlashToast(): void {
  const flash = useFlash()

  watch(
    flash,
    (value) => {
      if (!value?.banner) {
        return
      }

      if (value.bannerStyle === 'error') {
        toast.error(value.banner)
      } else {
        toast.success(value.banner)
      }
    },
    { immediate: true },
  )
}

export function useNavigation() {
  return computed(() => usePage().props.sidebar_navigation)
}

export function usePermission() {
  const user = useUser()

  return {
    has: (permission: string) => {
      return computed(() => user.value?.permissions?.includes(permission) ?? false)
    },
    hasAny: (...permissions: string[]) => {
      return computed(() => {
        if (!user.value?.permissions) {
          return false
        }
        return permissions.some((permission) => user.value?.permissions?.includes(permission))
      })
    },
    hasAll: (...permissions: string[]) => {
      return computed(() => {
        if (!user.value?.permissions) {
          return false
        }
        return permissions.every((permission) => user.value?.permissions?.includes(permission))
      })
    },
  }
}
